import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../UI/ThemeContext';

import './ThemePicker.scss';

/**
 * Gear button that opens a theme-colour picker. Selecting a theme updates the
 * app's main colour app-wide (via the --app-primary CSS variable).
 */
const ThemePicker: React.FC = () => {
  const { themes, themeKey, setThemeKey } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  return (
    <div className="theme-picker" ref={ref}>
      <button
        type="button"
        className="theme-picker__gear"
        aria-label="Theme colour"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <i className="pi pi-cog" />
      </button>

      {open && (
        <div className="theme-picker__panel" role="menu">
          <div className="theme-picker__head">
            <span className="theme-picker__title">
              <i className="pi pi-palette" />
              Theme Colour
            </span>
            <button
              type="button"
              className="theme-picker__close"
              aria-label="Close"
              onClick={() => setOpen(false)}
            >
              <i className="pi pi-times" />
            </button>
          </div>

          <ul className="theme-picker__list">
            {themes.map((t) => (
              <li key={t.key}>
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={t.key === themeKey}
                  className={`theme-picker__item${
                    t.key === themeKey ? ' is-active' : ''
                  }`}
                  onClick={() => {
                    // Apply the selected theme, then close the popup.
                    setThemeKey(t.key);
                    setOpen(false);
                  }}
                >
                  <span
                    className="theme-picker__swatch"
                    style={{ background: t.primary }}
                  />
                  <span className="theme-picker__label">{t.label}</span>
                  {t.key === themeKey && (
                    <i className="pi pi-check theme-picker__check" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ThemePicker;
